import { type RefObject, useEffect } from 'react';
import type { RouteObject, ShouldRevalidateFunctionArgs } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import { PROJECT_SETTINGS_DRAWER_PARAM } from '@gonfalon/navigator/navigators';

import { trackSettingsHeadingScrolled } from '../SettingsNavigation/settingsAnalytics';

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"], legend';
const HIDDEN_SELECTOR = '[aria-hidden="true"], [inert]';
const INTERACTIVE_SELECTOR = 'a, button, [role="tab"], [role="button"], input, select, textarea, label';
const TEXT_FALLBACK_AFTER_MS = 1000;
const GIVE_UP_AFTER_MS = 4000;

export function skipSettingsHeadingRevalidation(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const shouldRevalidate = (args: ShouldRevalidateFunctionArgs) => {
      const search = new URLSearchParams(args.currentUrl.search);
      if (
        !args.formMethod &&
        args.currentUrl.pathname === args.nextUrl.pathname &&
        args.currentUrl.hash === args.nextUrl.hash &&
        search.has('settings-heading') &&
        !args.nextUrl.searchParams.has('settings-heading')
      ) {
        search.delete('settings-heading');
        if (search.toString() === args.nextUrl.searchParams.toString()) {
          return false;
        }
      }
      return route.shouldRevalidate?.(args) ?? args.defaultShouldRevalidate;
    };
    if (route.index) {
      return { ...route, shouldRevalidate };
    }
    return {
      ...route,
      shouldRevalidate,
      children: route.children ? skipSettingsHeadingRevalidation(route.children) : undefined,
    };
  });
}

function findHeadingElement(root: HTMLElement, text: string) {
  return Array.from(root.querySelectorAll<HTMLElement>(HEADING_SELECTOR)).find(
    (element) => element.textContent?.trim() === text && !element.closest(HIDDEN_SELECTOR),
  );
}

function findTextElement(root: HTMLElement, text: string) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const element = node.parentElement;
    if (
      node.textContent?.trim() === text &&
      element &&
      !element.closest(INTERACTIVE_SELECTOR) &&
      !element.closest(HIDDEN_SELECTOR) &&
      element.getClientRects().length > 0
    ) {
      return element;
    }
  }
  return undefined;
}

function headingLevel(element: HTMLElement) {
  const tag = element.tagName.toLowerCase();
  if (/^h[1-6]$/.test(tag)) {
    return tag;
  }
  const ariaLevel = element.getAttribute('aria-level');
  return ariaLevel ? `h${ariaLevel}` : tag;
}

export function useScrollToSettingsHeading(
  scrollerRef: RefObject<HTMLElement | null>,
  { surface = 'page' }: { surface?: 'page' | 'drawer' } = {},
) {
  const location = useLocation();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);
  const isDrawerTarget = search.has(PROJECT_SETTINGS_DRAWER_PARAM);
  const heading = surface === 'page' && isDrawerTarget ? null : search.get('settings-heading');

  useEffect(() => {
    const root = scrollerRef.current;
    if (!heading || !root) {
      return;
    }

    const start = performance.now();
    let frame = 0;
    let timer = 0;
    let target: HTMLElement | undefined;
    let previousScrollHeight = -1;
    let previousTargetOffset: number | undefined;
    let previousScrollTop: number | undefined;
    let userScrolled = false;
    let tracked = false;
    const finish = () => {
      const url = new URL(window.location.href);
      if (url.pathname !== location.pathname || url.searchParams.get('settings-heading') !== heading) {
        return;
      }
      url.searchParams.delete('settings-heading');
      void navigate(
        { pathname: url.pathname, search: url.search, hash: url.hash },
        { replace: true, state: location.state },
      );
    };
    const attempt = () => {
      const elapsed = performance.now() - start;
      if (target && (!root.contains(target) || target.closest(HIDDEN_SELECTOR))) {
        target = undefined;
        previousScrollHeight = -1;
        previousTargetOffset = undefined;
      }
      if (!target) {
        const headingElement = findHeadingElement(root, heading);
        target = headingElement ?? (elapsed > TEXT_FALLBACK_AFTER_MS ? findTextElement(root, heading) : undefined);
        if (target && !tracked) {
          tracked = true;
          trackSettingsHeadingScrolled({
            heading,
            found: true,
            headingLevel: headingElement ? headingLevel(headingElement) : undefined,
            matchType: headingElement ? 'heading' : 'text',
            elapsedMs: Math.round(elapsed),
            surface,
          });
        }
      }
      if (target) {
        const targetOffset = root.scrollTop + target.getBoundingClientRect().top - root.getBoundingClientRect().top;
        if (previousScrollTop !== undefined && root.scrollTop !== previousScrollTop) {
          userScrolled = true;
        }
        if (!userScrolled && (root.scrollHeight !== previousScrollHeight || targetOffset !== previousTargetOffset)) {
          root.scrollTo({ top: targetOffset, behavior: 'instant' });
        }
        previousScrollHeight = root.scrollHeight;
        previousTargetOffset = targetOffset;
        previousScrollTop = root.scrollTop;
      }
      if (elapsed < GIVE_UP_AFTER_MS) {
        if (target) {
          timer = window.setTimeout(attempt, 100);
        } else {
          frame = requestAnimationFrame(attempt);
        }
      } else {
        if (!tracked) {
          tracked = true;
          trackSettingsHeadingScrolled({ heading, found: false, elapsedMs: Math.round(elapsed), surface });
        }
        finish();
      }
    };
    attempt();

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [heading, location, navigate, scrollerRef, surface]);
}
